import { useEffect } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { Button } from '$components'
import { Card } from '$components'
import { appointmentPublicCancelMutation } from '$api-client/@tanstack/react-query.gen'
import { Text } from '$components'
import { CheckIcon, WarningIcon, XIcon, LogoIcon } from '$components'
import { ToastHelper } from '$components'

export function CancelAppointmentPage() {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()

  const signature = searchParams.get('signature')
  const expires = searchParams.get('expires')

  const { mutate: cancelAppointment, isPending, isSuccess, isError } = useMutation({
    ...appointmentPublicCancelMutation(),
    onError: (error) => ToastHelper.error(error?.message || 'Failed to cancel appointment. Please contact the business.'),
  })

  const handleCancel = () => {
    if (!id || !signature || !expires) {
      ToastHelper.error('Invalid or expired link')
      return
    }

    cancelAppointment({
      path: { id: Number(id) },
      body: {
        signature: signature,
        expires: Number(expires),
      },
    })
  }

  useEffect(() => {
    if (!signature || !expires) {
      ToastHelper.error('Invalid or expired link')
    }
  }, [signature, expires])

  const isInvalidLink = !signature || !expires

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo/Branding */}
        <div className="flex justify-center mb-6">
          <LogoIcon className="h-6 w-auto text-indigo-600" />
        </div>

        {/* Main Card */}
        <Card rounding="xl">
          {!isSuccess && !isError && (
            <Card.Content className="p-8 sm:p-12 border border-neutral-100 rounded-3xl">
              <div className="flex flex-col items-center text-center mb-8">
                <XIcon className="size-10 text-red-600 mb-6" />

                <Text
                  type="h2"
                  className="text-gray-900 mb-3"
                >
                  Cancel Your Appointment
                </Text>

                <Text
                  type="s"
                  className="text-gray-600 max-w-sm"
                >
                  We&apos;re sorry to hear you need to cancel. The business will be notified immediately.
                </Text>
              </div>

              <Button
                onClick={handleCancel}
                isLoading={isPending}
                disabled={isPending || isInvalidLink}
                variant="primary"
                className="w-full h-12 text-base"
              >
                {isPending ? 'Cancelling...' : 'Cancel Appointment'}
              </Button>
            </Card.Content>
          )}

          {isSuccess && (
            <Card.Content className="p-8 sm:p-12 flex flex-col border border-neutral-100 items-center text-center rounded-3xl">
              <CheckIcon className="size-10 text-green-400 mb-6" />

              <Text
                type="h2"
                className="text-gray-900 mb-3"
              >
                Appointment Cancelled
              </Text>

              <Text
                type="s"
                className="text-gray-600 max-w-sm mb-8"
              >
                Your appointment has been successfully cancelled. The business has been notified and we hope to see you again soon.
              </Text>
            </Card.Content>
          )}

          {isError && (
            <Card.Content className="p-8 sm:p-12 flex flex-col border border-neutral-100 items-center text-center rounded-3xl">
              <WarningIcon className="size-10 text-red-400 mb-6" />

              <Text
                type="h2"
                className="text-gray-900 mb-3"
              >
                Unable to Cancel
              </Text>

              <Text
                type="s"
                className="text-gray-600 max-w-sm mb-8"
              >
                We couldn&apos;t cancel your appointment. Please contact the business directly for assistance.
              </Text>
            </Card.Content>
          )}
        </Card>

        {/* Footer Text */}
        <div className="text-center">
          <Text
            type="s"
            className="text-gray-600"
          >
            This is a secure cancellation link. Do not share it with others.
          </Text>
        </div>
      </div>
    </div>
  )
}
